import { difference, map, union } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LocationGroupScheduleComponentContext } from 'src/components/context';
import { ISearchMatch } from 'src/components/input';
import {
  SortMenuOption,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { ILocationScheduleComponentState } from 'src/features/locations/scheduleLocationGroupPage/modules/scheduleComponentDrawer/ComponentScheduleDrawer.hook';
import {
  useFilterAndSorting,
  useGetAllLayoutType,
  useGetPaginatedLocationGroupsList,
  useParamQuery,
} from 'src/hooks';
import { ILayout } from 'src/interfaces';
import { formatQuery } from 'src/utils';

import { IAdditionalLocationGroup } from './AdditionalLocationGroup.interface';
import {
  getAdditionalLocationGroupFilters,
  mergeLocationGroupList,
} from './AdditionalLocationGroups.helper';
import { LocationGroupRow } from './AdditionalLocationGroupsTable';
import useSearchMode from './useSearchMode';

export const useAdditionalLocationGroup = (layout: ILayout | undefined) => {
  const {
    daypartId,
    selectedLocationGroups,
    setSelectedLocationGroups,
    orientation,
    selectedLocationGroupId,
  } = useContext(LocationGroupScheduleComponentContext) || {};
  const { onChangeParams, getParamsValue, activeFilters } = useParamQuery({
    sort: ['name'],
    page: ['1'],
    pageSize: ['20'],
  });
  const location = useLocation();
  const rowsPerPage = Number(getParamsValue('pageSize')?.[0]) || 20;
  const currentPage = Number(getParamsValue('page')?.[0]) || 1;
  const [isMatchLoading, setIsMatchLoading] = useState<boolean>(false);
  const [matchedNames, setMatchedNames] = useState<ISearchMatch[]>([]);
  const [isAllPageSizeSelected, setIsAllPageSizeSelected] = useState(false);

  const {
    data: locationGroupList,
    isLoading: isLoadingLocationGroups,
    isFetching,
    isFetched,
  } = useGetPaginatedLocationGroupsList(
    formatQuery(
      location.search +
        (layout &&
          getAdditionalLocationGroupFilters(
            layout,
            orientation,
            selectedLocationGroupId,
            daypartId,
          )),
    ),
    true,
    {
      onSuccess: (data) => {
        const preCreateScheduleState = JSON.parse(
          localStorage.getItem('createScheduleState') || '{}',
        ) as ILocationScheduleComponentState;

        localStorage.setItem(
          'createScheduleState',
          JSON.stringify({
            ...preCreateScheduleState,
            locationGroupList: mergeLocationGroupList(
              preCreateScheduleState?.locationGroupList,
              data.locationGroups,
            ),
          }),
        );
      },
    },
  );

  const { data: layoutTypes = [] } = useGetAllLayoutType();

  const locationGroups = useMemo(
    () =>
      locationGroupList?.locationGroups?.map(
        (group) =>
          ({
            ...group,
            layoutType:
              layoutTypes.find((type) => type.id === group.layout_type_id)
                ?.name || '',
            isSelected: false,
          } as IAdditionalLocationGroup),
      ) ?? [],
    [locationGroupList?.locationGroups, layoutTypes],
  );

  const initialFuzzy = !('name.list' in activeFilters);
  const {
    isFuzzySearch,
    onSearchTypeChange,
    onChangeKeyword,
    onRemoveKeyword,
    onReset,
  } = useSearchMode(initialFuzzy, useFilterAndSorting);

  const isAllSelected = useMemo(() => {
    if (!locationGroups.length) return false;

    return locationGroups.every((g) => selectedLocationGroups?.has(g.id));
  }, [locationGroups, selectedLocationGroups]);

  const totalCount = locationGroupList?.totalCount ?? 0;

  useEffect(() => {
    if (
      isAllPageSizeSelected &&
      isFetched &&
      totalCount > 0 &&
      totalCount !== rowsPerPage
    ) {
      onChangeParams('pageSize', totalCount.toString());
    }
  }, [
    isAllPageSizeSelected,
    onChangeParams,
    rowsPerPage,
    totalCount,
    isFetched,
  ]);

  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      if (value !== 1) {
        onChangeParams('page', [value.toString()]);
      } else {
        onChangeParams('page', []);
      }
    },
    [onChangeParams],
  );

  const handleRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      if (newRowsPerPage !== 20) {
        onChangeParams('pageSize', [newRowsPerPage.toString()]);
        if (newRowsPerPage === totalCount) {
          setIsAllPageSizeSelected(true);
        } else {
          setIsAllPageSizeSelected(false);
        }
      } else {
        setIsAllPageSizeSelected(false);
        onChangeParams('pageSize', []);
      }
      onChangeParams('page', []);
    },
    [onChangeParams, totalCount],
  );

  const selectedSortOption: SortMenuOption = {
    option: getParamsValue('sort')[0]?.replace('-', '') as SortOptions,
    order: getParamsValue('sort')[0]?.includes('-') ? 'desc' : 'asc',
  };

  const onSortChangeHandle = (selectedOption: {
    option: SortOptions;
    order: 'desc' | 'asc';
  }) => {
    onChangeParams('sort', [
      `${selectedOption.order === 'desc' ? '-' : ''}${selectedOption.option}`,
    ]);
  };

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!locationGroups || locationGroups.length === 0) return;

      setSelectedLocationGroups((prev) => {
        const idsOnCurrentPage = map(locationGroups, 'id');
        const prevArray = Array.from(prev);
        const nextArray = checked
          ? union(prevArray, idsOnCurrentPage)
          : difference(prevArray, idsOnCurrentPage);

        return new Set(nextArray);
      });
    },
    [locationGroups, setSelectedLocationGroups],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedLocationGroups(new Set());
  }, [setSelectedLocationGroups]);

  const handleSelectLocationGroup = useCallback(
    (id: LocationGroupRow['id'], checked: boolean) => {
      const newSelected = new Set(selectedLocationGroups);

      if (!checked) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      setSelectedLocationGroups(newSelected);
    },
    [selectedLocationGroups, setSelectedLocationGroups],
  );

  const nameListPrams = useMemo(
    () => (getParamsValue('name.list') as string[]) || [],
    [getParamsValue],
  );

  useEffect(() => {
    const updateMatchedNames = () => {
      const updated = nameListPrams.map((name) => ({
        name,
        matched: locationGroupList
          ? locationGroupList.locationGroups.some(
              (a) => a.name.toLowerCase() === name.toLowerCase(),
            )
          : false,
      }));
      setMatchedNames(updated);
    };

    if (!isFuzzySearch && nameListPrams.length) {
      setIsMatchLoading(true);
    }

    if (
      (locationGroupList?.locationGroups?.length ||
        (!isLoadingLocationGroups &&
          locationGroupList?.locationGroups?.length === 0)) &&
      !isFetching
    ) {
      updateMatchedNames();
      setIsMatchLoading(false);
    }
  }, [
    isFuzzySearch,
    isLoadingLocationGroups,
    isFetching,
    locationGroupList,
    nameListPrams,
  ]);

  return {
    onChangeParams,
    onSortChangeHandle,
    selectedSortOption,
    locationGroups,
    locationGroupList,
    isLoadingLocationGroups,
    handlePageChange,
    currentPage,
    rowsPerPage,
    handleRowsPerPageChange,
    isAllSelected,
    handleSelectAll,
    handleClearSelection,
    selectedLocationGroups,
    handleSelectLocationGroup,
    isFuzzySearch,
    onSearchTypeChange,
    onChangeKeyword,
    onRemoveKeyword,
    onReset,
    matchedNames,
    isMatchLoading,
    isFetching,
  };
};
